import { useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { AlertDescription } from '@/components/ui/alert-description';
import { Loader2, AlertCircle } from 'lucide-react';
import { useMap } from './map-provider';
import { MAPS_CONFIG } from '@/config/maps';
import { FallbackMap } from './fallback-map';

interface MapViewProps {
  center?: { lat: number; lng: number };
  markers?: Array<{
    position: { lat: number; lng: number };
    title?: string;
  }>;
  zoom?: number;
}

export function MapView({ 
  center = MAPS_CONFIG.defaultCenter,
  markers = [],
  zoom = MAPS_CONFIG.defaultZoom 
}: MapViewProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const { isLoaded, error } = useMap();

  useEffect(() => {
    if (!isLoaded || !mapRef.current || error) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        center,
        zoom,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
    } else {
      mapInstanceRef.current.setCenter(center);
      mapInstanceRef.current.setZoom(zoom);
    }

    const googleMarkers = markers.map(markerData => 
      new google.maps.Marker({
        position: markerData.position,
        map: mapInstanceRef.current,
        title: markerData.title
      })
    );

    return () => {
      googleMarkers.forEach(marker => marker.setMap(null));
    };
  }, [isLoaded, center, markers, zoom, error]);

  if (!MAPS_CONFIG.isEnabled) {
    return <FallbackMap />;
  }

  if (error) {
    return (
      <Alert variant="error">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error.toString()}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="relative w-full h-[600px] overflow-hidden">
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-50">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </Card>
  );
}